const EMPTY_DECISIONS = Object.freeze({
  preferences: 'unknown',
  analytics: 'unknown',
  marketing: 'unknown',
  saleOfData: 'unknown',
});

const decision = value => value === 'yes' ? 'granted' : value === 'no' ? 'denied' : 'unknown';

function consentmoHasSavedChoice(consentmo) {
  try {
    if (!consentmo || typeof consentmo.currentVisitorConsent !== 'function') return false;
    const choice = consentmo.currentVisitorConsent();
    return choice?.necessary === true
      && typeof choice.analytics === 'boolean'
      && typeof choice.marketing === 'boolean'
      && typeof choice.preferences === 'boolean';
  } catch {
    return false;
  }
}

function immutableSnapshot(status, values = {}) {
  const decisions = Object.freeze({ ...EMPTY_DECISIONS, ...(values.decisions || {}) });
  return Object.freeze({
    status,
    recentlyViewedAllowed: false,
    preferencesAffirmative: false,
    analyticsAffirmative: false,
    marketingAffirmative: false,
    saleOfDataAllowed: false,
    advertisingAffirmative: false,
    ...values,
    decisions,
  });
}

export function readCustomerPrivacy(customerPrivacy, consentmo) {
  if (!customerPrivacy) return immutableSnapshot('unavailable');
  try {
    const recorded = customerPrivacy.currentVisitorConsent();
    if (!recorded || typeof recorded !== 'object') return immutableSnapshot('error');
    const decisions = {
      preferences: decision(recorded.preferences),
      analytics: decision(recorded.analytics),
      marketing: decision(recorded.marketing),
      saleOfData: decision(recorded.sale_of_data),
    };
    const recentlyViewedAllowed = [recorded.preferences, recorded.analytics, recorded.marketing]
      .every(value => value === 'yes' || value === 'no')
      && consentmoHasSavedChoice(consentmo);
    const preferencesAffirmative = customerPrivacy.preferencesProcessingAllowed() === true && recorded.preferences === 'yes';
    const analyticsAffirmative = customerPrivacy.analyticsProcessingAllowed() === true && recorded.analytics === 'yes';
    const marketingAffirmative = customerPrivacy.marketingAllowed() === true && recorded.marketing === 'yes';
    const saleOfDataAllowed = customerPrivacy.saleOfDataAllowed() === true && recorded.sale_of_data !== 'no';
    return immutableSnapshot('ready', {
      decisions,
      recentlyViewedAllowed,
      preferencesAffirmative,
      analyticsAffirmative,
      marketingAffirmative,
      saleOfDataAllowed,
      advertisingAffirmative: marketingAffirmative && saleOfDataAllowed,
    });
  } catch {
    return immutableSnapshot('error');
  }
}

export function createCustomerPrivacyBridge({
  shopify,
  eventTarget = globalThis.document,
  getConsentmo = () => globalThis.Consentmo,
} = {}) {
  let snapshot = immutableSnapshot('loading');
  let shopifyReady = false;
  const listeners = new Set();
  const providerEventNames = new Set();

  const publish = next => {
    snapshot = next;
    for (const subscription of [...listeners]) subscription.deliver(snapshot);
  };
  const resolveConsentmo = () => {
    try { return typeof getConsentmo === 'function' ? getConsentmo() : undefined; }
    catch { return undefined; }
  };
  const listenForProviderEvent = name => {
    if (typeof name !== 'string' || name.length === 0 || providerEventNames.has(name)) return;
    providerEventNames.add(name);
    eventTarget?.addEventListener(name, onConsentmo);
  };
  const bindProviderEvents = () => {
    try {
      const events = resolveConsentmo()?.events;
      listenForProviderEvent(events?.Initialized);
      listenForProviderEvent(events?.ConsentUpdate);
    } catch { /* A malformed provider event surface cannot grant history. */ }
  };
  const refresh = () => {
    bindProviderEvents();
    if (!shopifyReady) return;
    publish(readCustomerPrivacy(shopify?.customerPrivacy, resolveConsentmo()));
  };
  const onConsent = () => refresh();
  function onConsentmo() { refresh(); }

  const capability = Object.freeze({
    snapshot: () => snapshot,
    subscribe(listener, options = {}) {
      if (typeof listener !== 'function') throw new TypeError('A customer privacy listener must be a function.');
      if (options.signal?.aborted) return () => undefined;
      let active = true;
      const subscription = {
        deliver(next) {
          if (!active) return;
          if (options.signal?.aborted) { unsubscribe(); return; }
          try { listener(next); } catch { /* A consumer cannot break privacy state delivery. */ }
        },
      };
      const unsubscribe = () => {
        if (!active) return;
        active = false;
        listeners.delete(subscription);
        options.signal?.removeEventListener('abort', unsubscribe);
      };
      listeners.add(subscription);
      options.signal?.addEventListener('abort', unsubscribe, { once: true });
      if (options.signal?.aborted) unsubscribe();
      else subscription.deliver(snapshot);
      return unsubscribe;
    },
  });

  if (!shopify || typeof shopify.loadFeatures !== 'function') {
    publish(immutableSnapshot('unavailable'));
    return capability;
  }
  listenForProviderEvent('consentmoSignal_onLoad');
  listenForProviderEvent('consentmoSignal');
  listenForProviderEvent('consentmo:dialogOpened');
  listenForProviderEvent('consentmo:dialogClosed');
  bindProviderEvents();
  try {
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      if (error) { publish(immutableSnapshot('error')); return; }
      shopifyReady = true;
      eventTarget?.addEventListener('visitorConsentCollected', onConsent);
      refresh();
    });
  } catch {
    publish(immutableSnapshot('error'));
  }
  return capability;
}

function installBrowserCapability() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.FoldedLabelCustomerPrivacy) return;
  const capability = createCustomerPrivacyBridge({
    shopify: window.Shopify,
    eventTarget: document,
    getConsentmo: () => window.Consentmo,
  });
  Object.defineProperty(window, 'FoldedLabelCustomerPrivacy', {
    value: capability,
    configurable: false,
    enumerable: true,
    writable: false,
  });
  document.dispatchEvent(new CustomEvent('foldedlabel:customer-privacy-ready'));
}

installBrowserCapability();
